/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import {
  ArrayExtensions,
  generateImageUrl,
} from "@msdyn365-commerce-modules/retail-actions";
import {
  getPayloadObject,
  getTelemetryAttributes,
  getTelemetryObject,
  IPayLoad,
  ITelemetryContent,
  Module,
  Node,
  onTelemetryClick,
} from "@msdyn365-commerce-modules/utilities";
import {
  IImageData,
  IImageSettings,
  Image,
  ICoreContext,
} from "@msdyn365-commerce/core";
import classnames from "classnames";
import * as React from "react";
import {
  INavigationMenuViewProps,
  IMenuItemData,
  INavigationMenuResources,
} from "@msdyn365-commerce-modules/navigation-menu/";

interface INavigationState {
  parentMenu?: number;
  activeMenu?: number;
  categoryImage?: IImageData[] | null;
  mobileViewLabelText?: string;
  categoryImageAltText: string;
}
interface INavigationMenuResourcesClone extends INavigationMenuResources {
  navigationActive: string;
  navigationSubmenu: string;
  navigationContent: string;
  submenuContent: string;
}

interface INavigationMenuViewPropsClone extends INavigationMenuViewProps {
  resources: INavigationMenuResourcesClone;
}
/**
 *
 * NavigationMenuView component
 * @extends {React.PureComponent<INavigationMenuViewProps>}
 */
export class NavigationMenuView extends React.PureComponent<
  INavigationMenuViewPropsClone,
  INavigationState
> {
  private static isBackTrack: boolean = false;
  private currentLevel: number = 0;
  private menuNode: React.RefObject<HTMLUListElement>;
  private telemetryContent: ITelemetryContent;
  private payLoad: IPayLoad;
  constructor(props: INavigationMenuViewPropsClone) {
    super(props);
    this.menuNode = React.createRef();
    this.state = {
      activeMenu: undefined,
      mobileViewLabelText: "",
      parentMenu: undefined,
      categoryImageAltText: "",
    };
    this._closeSubmenu = this._closeSubmenu.bind(this);
    this.telemetryContent = getTelemetryObject(
      this.props.context.request.telemetryPageName!,
      this.props.friendlyName,
      this.props.telemetry,
    );
    this.payLoad = getPayloadObject("click", this.telemetryContent, "", "");
  }

  public componentDidMount(): void {
    document.body &&
      document.body.addEventListener("mousedown", this._handleClickOutside);
  }

  public componentWillUnmount(): void {
    document.body &&
      document.body.removeEventListener(
        "mousedown",
        this._handleClickOutside,
        false,
      );
  }

  public render(): JSX.Element | null {
    const {
      isMobileView,
      MenuList,
      MobileBackButton,
      MobileDescriptionContainer,
      MobileDescriptionLabel,
      Navigation,
    } = this.props;
    //this.props.context
    this.currentLevel = 1;
    return (
      <Module
        {...Navigation}
        className={classnames(
          Navigation.className,
          isMobileView && this.state.activeMenu !== undefined
            ? "child"
            : "parent",
        )}
      >
        <Node {...MenuList} ref={this.menuNode} tabIndex="-1">
          {isMobileView && this.state.activeMenu !== undefined && (
            <Node {...MobileDescriptionContainer}>
              <Node {...MobileBackButton} onClick={this._handleGoBack()} />
              <Node {...MobileDescriptionLabel}>
                {this.state.mobileViewLabelText}
              </Node>
            </Node>
          )}
          {this._renderDisplay(this.props.context)}
        </Node>
      </Module>
    );
  }

  private _renderDisplay(
    context: ICoreContext<{
      [x: string]: any;
    }>,
  ): JSX.Element[] {
    const {ListItem, menuItemData, isMobileView} = this.props;

    typeof localStorage !== "undefined"
      ? localStorage.setItem("menu", JSON.stringify(menuItemData))
      : null;
    const {activeMenu} = this.state;
    const menuItemList: JSX.Element[] = [];

    if (isMobileView && activeMenu !== undefined && menuItemData.length > 0) {
      let menuItem: IMenuItemData = {};
      for (let i = 0; i < menuItemData.length; i++) {
        if (menuItemData[i] && menuItemData[i].id === activeMenu) {
          menuItem = menuItemData[i];
          !NavigationMenuView.isBackTrack
            ? this.setState({parentMenu: undefined})
            : this.setState({
                parentMenu: undefined,
                mobileViewLabelText: menuItemData[i].linkText,
              });
          break;
        }
        menuItem = this._getFromSubMenu(menuItemData[i]) as IMenuItemData;
        if (menuItem && menuItem.id === activeMenu) {
          break;
        }
      }

      menuItem &&
        menuItemList.push(
          <Node key={menuItem.id} {...ListItem}>
            {" "}
            {this._createMenuItemList(menuItem, context)}{" "}
          </Node>,
        );
    } else {
      menuItemData.forEach((item: IMenuItemData, index: number) => {
        menuItemList.push(
          <Node key={index} {...ListItem}>
            {this._createMenuItemList(item, context)}
          </Node>,
        );
      });
    }

    return menuItemList;
  }

  private _getFromSubMenu(item?: IMenuItemData): IMenuItemData | null {
    const subMenus = item && item.subMenu;
    if (subMenus && subMenus.length > 0) {
      for (let i = 0; i <= subMenus.length - 1; i++) {
        if (subMenus[i].id === this.state.activeMenu) {
          !NavigationMenuView.isBackTrack
            ? this.setState({parentMenu: item && item.id})
            : this.setState({
                parentMenu: item && item.id,
                mobileViewLabelText: subMenus[i].linkText,
              });
          return subMenus[i];
        }
        const found = this._getFromSubMenu(subMenus[i]);
        if (found) {
          return found;
        }
      }
    }
    this.props.context.telemetry.error(
      this.props.resources.submenuContent,
      // "Submenu content is empty, module wont render."
    );
    return null;
  }

  private _createMenuItemList(
    menuItemData: IMenuItemData,
    context: ICoreContext<{
      [x: string]: any;
    }>,
  ): JSX.Element | null {
    if (
      menuItemData &&
      menuItemData.subMenu &&
      menuItemData.subMenu.length > 0
    ) {
      if (this.props.isMobileView && this.state.activeMenu !== undefined) {
        return this._renderSubMenu(
          menuItemData.subMenu,
          menuItemData.id,
          false,
          context,
        );
      } else {
        return (
          <React.Fragment>
            {this._renderButtonMenuItem(menuItemData)}
            {this._renderSubMenu(
              menuItemData.subMenu,
              menuItemData.id,
              false,
              context,
            )}
          </React.Fragment>
        );
      }
    } else if (
      menuItemData &&
      menuItemData.linkText &&
      menuItemData.linkURL &&
      menuItemData.linkURL.length > 0
    ) {
      return this._renderLinkMenuItem(menuItemData, menuItemData.id, false);
    } else if (menuItemData && menuItemData.linkText && !menuItemData.linkURL) {
      return this._renderSpanMenuItem(menuItemData);
    }
    this.props.context.telemetry.error(
      this.props.resources.navigationContent,
      // "Navigation menu content is empty, module wont render."
    );
    return null;
  }

  private _renderSubMenu(
    subMenus?: IMenuItemData[],
    activeMenu?: number,
    IsSubMenu?: boolean,
    context?: ICoreContext<{
      [x: string]: any;
    }>,
  ): JSX.Element | null {
    const {isMobileView, ListItem} = this.props;
    const enableMultiSupportMenu =
      this.props.config.enableMultilevelMenu || false;
    const multiLevelSupportedMenu = this.props.config.menuLevelSupport || 3;
    // const isParentMenu:boolean= false;
    if (activeMenu && this.state.activeMenu !== activeMenu) {
      this.props.context.telemetry.error(
        this.props.resources.navigationActive,
        // "Navigation Active menu content is empty, module wont render."
      );
      return null;
    }

    if (!subMenus || subMenus.length === 0) {
      this.props.context.telemetry.error(
        this.props.resources.navigationSubmenu,
        // "Navigation Submenu content is empty, module wont render."
      );
      return null;
    }

    let levelClassName: string = "";
    const menuOptions =
      subMenus &&
      subMenus.map((option: IMenuItemData, idx: number) => {
        const hasOptions = option.subMenu && option.subMenu.length > 0;
        let menuItem: JSX.Element | null;
        if (hasOptions && isMobileView) {
          menuItem = this._renderButtonMenuItem(option, activeMenu, idx);
        } else {
          menuItem = option.linkURL
            ? this._renderLinkMenuItem(option, idx, true, context)
            : this._renderSpanMenuItem(option);
        }

        let subMenu;
        const haveSubmenu =
          hasOptions &&
          enableMultiSupportMenu &&
          this.currentLevel <= Math.round(multiLevelSupportedMenu) - 1;
        if (haveSubmenu) {
          this.currentLevel++;
          levelClassName = enableMultiSupportMenu
            ? `level-${this.currentLevel.toString()}`
            : "";
          subMenu = this._renderSubMenu(
            option.subMenu,
            isMobileView ? option.id : undefined,
            true,
          );
        }
        return (
          <Node
            {...ListItem}
            key={option.id}
            className={classnames(
              ListItem.className,
              haveSubmenu && "havesubmenu",
            )}
          >
            {menuItem}
            {subMenu}
          </Node>
        );
      });
    return this._renderMenu(levelClassName, menuOptions, activeMenu, IsSubMenu);
  }
  private _renderButtonMenuItem(
    option: IMenuItemData,
    activeMenu?: number,
    index?: number,
  ): JSX.Element | null {
    const {Button} = this.props;
    return (
      <Node
        key={index}
        {...Button}
        onClick={this._handleDropdownToggle(option, activeMenu)}
        onFocus={this._closeSubmenu}
        aria-haspopup={true}
        aria-expanded={
          this.state.activeMenu && this.state.activeMenu === option.id
            ? true
            : false
        }
        data-parent={activeMenu}
      >
        {option.linkText}
      </Node>
    );
  }

  private _renderLinkMenuItem(
    option: IMenuItemData,
    index?: number,
    hoverEffect: boolean = true,
    context?: ICoreContext<{
      [x: string]: any;
    }>,
  ): JSX.Element | null {
    const {Link} = this.props;
    const linkText = option.linkText ? option.linkText : "";
    const imagesource = option.imageSource ? option.imageSource : "";
    this.payLoad.contentAction.etext = linkText;
    const attributes = getTelemetryAttributes(
      this.telemetryContent,
      this.payLoad,
    );

    const baseImageURL = context?.request.apiSettings.baseImageUrl.slice(0, -1); //url comes with / in the end which we don't need, so we remove it
    const imageSource = `${baseImageURL}${linkText}_000_001.jpg`;

    return (
      <Node
        {...Link}
        key={index}
        onMouseOver={
          hoverEffect && this._updateCategoryImage(imagesource, option)
        }
        href={option.linkURL}
        {...attributes}
        onClick={onTelemetryClick(
          this.telemetryContent,
          this.payLoad,
          linkText,
        )}
      >
        <Image
          gridSettings={this.props.context.request.gridSettings!}
          requestContext={this.props.context.actionContext.requestContext!}
          src={imageSource}
          className="ms-nav_menu_item_image"
          loadFailureBehavior="hide"
        />
        {option.linkText}
      </Node>
    );
  }

  private _renderPromotionalLink(
    linkText?: string,
    linkUrl?: string,
  ): JSX.Element | null {
    const {Link} = this.props;
    this.payLoad.contentAction.etext = linkText;
    const attributes = getTelemetryAttributes(
      this.telemetryContent,
      this.payLoad,
    );
    if (linkText && linkUrl) {
      return (
        <Node
          {...Link}
          href={linkUrl}
          {...attributes}
          onClick={onTelemetryClick(
            this.telemetryContent,
            this.payLoad,
            linkText,
          )}
        >
          {linkText}
        </Node>
      );
    } else {
      return null;
    }
  }
  private _renderSpanMenuItem(
    option: IMenuItemData,
    index?: number,
  ): JSX.Element | null {
    const {Span} = this.props;
    return (
      <Node key={index} {...Span}>
        {option.linkText}
      </Node>
    );
  }
  // tslint:disable-next-line: cyclomatic-complexity
  private _renderMenu(
    level: string,
    menuOptions: JSX.Element[],
    currentItem?: number,
    submenu?: boolean,
  ): JSX.Element | null {
    const {
      DivContainer,
      MenuList,
      ImageDivContainer,
      showCategoryImage,
      showPromotionalContent,
      isMobileView,
    } = this.props;
    const categoryImageDisplay =
      !isMobileView &&
      (showCategoryImage || showPromotionalContent) &&
      !ArrayExtensions.hasElements(this.state.categoryImage) &&
      !submenu;
    const promotionalContentDisplay =
      !isMobileView &&
      showPromotionalContent &&
      ArrayExtensions.hasElements(this.state.categoryImage) &&
      !submenu;
    const DivContainerClass =
      this.currentLevel > 2 || categoryImageDisplay
        ? DivContainer!.className
        : "ms-nav__deafult";
    this.currentLevel = 1;
    return (
      <Node {...DivContainer} className={DivContainerClass}>
        <Node
          {...MenuList}
          className={classnames(
            MenuList.className,
            level,
            categoryImageDisplay && "havecateImage",
            categoryImageDisplay &&
              this.props.config.menuLevelSupport &&
              this.props.config.menuLevelSupport > 2 &&
              "navmenu-multi-level",
          )}
        >
          {menuOptions}
        </Node>
        {categoryImageDisplay &&
          this.state.categoryImage &&
          this.state.categoryImage.map((item: IImageData) => (
            <Node
              {...ImageDivContainer}
              className={ImageDivContainer!.className}
            >
              {this.state.categoryImage &&
                this._getCategoryImage(item.src, item.altText?.toString())}
              {promotionalContentDisplay &&
                this._renderPromotionalLink(
                  item.altText,
                  item.additionalProperties?.linkUrl,
                )}
            </Node>
          ))}
      </Node>
    );
  }
  private _updateCategoryImage = (
    categoryImageSrc: string,
    option: IMenuItemData,
  ) => () => {
    const linkText = option && option.linkText ? option.linkText : "";
    const promotionalImage: IImageData[] = [
      {src: categoryImageSrc, altText: linkText},
    ];
    // Read category and promotional image in one array
    if (ArrayExtensions.hasElements(option.promotionalContent)) {
      option.promotionalContent.map((item) => {
        if (item && item.image) {
          const imageSrc = item.image.src;
          promotionalImage.push({
            src: imageSrc,
            altText: item.text,
            additionalProperties: {linkUrl: item.linkUrl.destinationUrl},
          });
        }
      });
    }
    this.setState({
      categoryImage:
        promotionalImage.length > 0 ? promotionalImage : [{src: "empty"}],
      categoryImageAltText: linkText,
    });
  };
  private _handleDropdownToggle = (
    data: IMenuItemData,
    parentId?: number,
  ) => () => {
    if (!this.props.isMobileView) {
      this.setState({
        activeMenu:
          this.state.activeMenu && this.state.activeMenu === data.id!
            ? undefined
            : data.id!,
        parentMenu: parentId,
      });
      if (this.props.showCategoryImage) {
        this._updateCategoryImage(data.imageSource!, data)();
      }
    } else {
      NavigationMenuView.isBackTrack = false;
      this.setState({
        activeMenu: data.id,
        mobileViewLabelText: data.linkText!,
        parentMenu: parentId,
      });
    }

    this._resetFocus();
  };

  private _handleGoBack = () => () => {
    NavigationMenuView.isBackTrack = true;
    this.setState({activeMenu: this.state.parentMenu});

    this._resetFocus();
  };

  private _resetFocus = () => {
    if (this.props.isMobileView) {
      setTimeout(() => {
        this.menuNode && this.menuNode.current && this.menuNode.current.focus();
      }, 0);
    }
  };

  // tslint:disable-next-line:no-any
  private _handleClickOutside = (event: any) => {
    if (
      this.menuNode.current &&
      !this.menuNode.current.contains(event.target)
    ) {
      this.setState({
        activeMenu: undefined,
        mobileViewLabelText: "",
        categoryImage: null,
      });
    }
  };

  private _closeSubmenu(): void {
    if (!this.props.isMobileView) {
      this.setState({
        activeMenu: undefined,
        categoryImage: null,
        mobileViewLabelText: "",
      });
    }
  }
  private _getCategoryImage = (
    categoryImage: string | undefined,
    alttext: string | undefined,
  ): React.ReactNode | null => {
    const categoryImageUrl = generateImageUrl(
      `${categoryImage}`,
      this.props.context.actionContext.requestContext.apiSettings,
    );
    const defaultImageSettings: IImageSettings = {
      viewports: {
        xs: {q: "w=300&h=250&m=8", w: 0, h: 0},
        sm: {q: "w=300&h=250&m=8", w: 0, h: 0},
        md: {q: "w=300&h=250&m=8", w: 0, h: 0},
        lg: {q: "w=300&h=250&m=8", w: 0, h: 0},
      },
      lazyload: true,
    };
    if (categoryImageUrl !== undefined) {
      const ImageData: IImageData = {src: categoryImageUrl};
      return (
        <Image
          className="ms-nav-image__item"
          {...ImageData}
          gridSettings={this.props.context.request.gridSettings!}
          imageSettings={
            this.props.config.categoryImageSettings || defaultImageSettings
          }
          loadFailureBehavior="hide"
          role="tabpanel"
          id={`categoryImageTag__categoryImage`}
          altText={alttext}
        />
      );
    }
    return null;
  };
}

export default NavigationMenuView;
